/**
 * Maakt aggregate_project-responses UI-veilig (ontbrekende arrays/velden).
 */
import type { AggregationResult, BuildingPartSummaryRow, PricedLine } from './types';

export function normalizeAggregationApiResponse(
  raw: Record<string, unknown>,
  fallbackProjectId: string
): AggregationResult {
  const systemsRaw = Array.isArray(raw.systems) ? raw.systems : [];
  const systems: AggregationResult['systems'] = [];

  for (const row of systemsRaw) {
    if (row == null || typeof row !== 'object') continue;
    const r = row as Record<string, unknown>;
    const pricingRaw = r.pricing && typeof r.pricing === 'object' ? (r.pricing as Record<string, unknown>) : {};
    const linesRaw = Array.isArray(pricingRaw.lines) ? pricingRaw.lines : [];
    const lines: PricedLine[] = [];
    for (const ln of linesRaw) {
      if (ln == null || typeof ln !== 'object') continue;
      const l = ln as Record<string, unknown>;
      lines.push({
        type: typeof l.type === 'string' ? l.type : undefined,
        description: String(l.description ?? '—'),
        quantity: Number(l.quantity) || 0,
        unit: String(l.unit ?? '—'),
        unit_price: Number(l.unit_price) || 0,
        total: Number(l.total) || 0,
        evidence_grade: typeof l.evidence_grade === 'string' ? l.evidence_grade : undefined,
      });
    }
    const sid = String(r.system_id ?? '').trim();
    const vid = String(r.variant_id ?? '').trim();
    if (!sid || !vid) continue;
    systems.push({
      line_id: typeof r.line_id === 'string' && r.line_id.trim() ? r.line_id.trim() : undefined,
      system_id: sid,
      variant_id: vid,
      quantity: Number(r.quantity) || 0,
      part_label: typeof r.part_label === 'string' ? r.part_label : undefined,
      subtotal: Number(r.subtotal) || 0,
      pricing: {
        lines,
        subtotal_materials:
          typeof pricingRaw.subtotal_materials === 'number' ? pricingRaw.subtotal_materials : undefined,
        subtotal_labor: typeof pricingRaw.subtotal_labor === 'number' ? pricingRaw.subtotal_labor : undefined,
        subtotal_equipment:
          typeof pricingRaw.subtotal_equipment === 'number' ? pricingRaw.subtotal_equipment : undefined,
      },
    });
  }

  const partsRaw = Array.isArray(raw.building_part_summary) ? raw.building_part_summary : [];
  const building_part_summary: BuildingPartSummaryRow[] = [];
  for (const p of partsRaw) {
    if (p == null || typeof p !== 'object') continue;
    const x = p as Record<string, unknown>;
    const pk = String(x.part_key ?? x.part_label ?? '').trim();
    const pl = String(x.part_label ?? pk ?? 'Deel').trim();
    if (!pl) continue;
    building_part_summary.push({
      part_key: pk || pl,
      part_label: pl,
      total_materials: Number(x.total_materials) || 0,
      total_labor: Number(x.total_labor) || 0,
      total_equipment: Number(x.total_equipment) || 0,
      subtotal: Number(x.subtotal) || 0,
      practical_corrections_allocated: Number(x.practical_corrections_allocated) || 0,
      total_with_corrections: Number(x.total_with_corrections) || 0,
      total_co2_kg: typeof x.total_co2_kg === 'number' ? x.total_co2_kg : undefined,
    });
  }

  return {
    project_id: String(raw.project_id ?? fallbackProjectId),
    total: Number(raw.total) || 0,
    total_materials: Number(raw.total_materials) || 0,
    total_labor: Number(raw.total_labor) || 0,
    total_equipment: Number(raw.total_equipment) || 0,
    total_co2_kg: typeof raw.total_co2_kg === 'number' ? raw.total_co2_kg : undefined,
    building_part_summary: building_part_summary.length > 0 ? building_part_summary : undefined,
    systems,
  };
}
